import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  Card,
  CardContent,
  Grid
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';
import Spinner from '../components/Spinner';

interface GradeItem {
  _id: string;
  assignment: { title: string; subject?: { _id: string; name: string } };
  score?: number;
  maxScore?: number;
  status: string;
  feedback: string;
  gradedAt?: string;
}

const StudentGrades: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [grades, setGrades] = useState<GradeItem[]>([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState<string | null>(null);

  const isHighSchoolEducation = (educationLevel?: string) =>
    typeof educationLevel === 'string' && /high[- ]school/i.test(educationLevel);

  const hasSchoolWorkAccess = Boolean(
    isHighSchoolEducation(user?.educationLevel) &&
    (user?.school || user?.schoolName || user?.schoolEmail)
  );

  useEffect(() => {
    const fetchGrades = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get('/school-work/grades');
        setGrades(Array.isArray(res.data) ? res.data : []);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Unable to load grades');
      } finally {
        setLoading(false);
      }
    };
    
    if (hasSchoolWorkAccess) {
      fetchGrades();
    } else {
      setLoading(false);
    }
  }, [hasSchoolWorkAccess, user?._id]);

  if (!hasSchoolWorkAccess && !loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          School Work is available only to high school students...
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Grades & Marks</Typography>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/school-work')}>
          Back to Dashboard
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <Spinner />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      ) : (
        <Box>
          <Typography variant="subtitle1" sx={{ mb: 2 }}>Grades and feedback</Typography>
          {grades.length === 0 ? (
            <Alert severity="info">No graded assignments found yet.</Alert>
          ) : !selectedSubjectId ? (
            <Grid container spacing={3}>
              {Object.values(
                grades.reduce((acc, grade) => {
                  const subject = grade.assignment?.subject;
                  if (subject) {
                    if (!acc[subject._id]) {
                      acc[subject._id] = { ...subject, count: 0 };
                    }
                    acc[subject._id].count += 1;
                  } else {
                    if (!acc['general']) {
                      acc['general'] = { _id: 'general', name: 'General/Uncategorized', count: 0 };
                    }
                    acc['general'].count += 1;
                  }
                  return acc;
                }, {} as Record<string, { _id: string; name: string; count: number }>)
              ).map((subject: any) => (
                <Grid item xs={12} sm={6} md={4} key={subject._id}>
                  <Card 
                    sx={{ cursor: 'pointer', transition: '0.2s', '&:hover': { transform: 'translateY(-4px)', boxShadow: 6 } }}
                    onClick={() => setSelectedSubjectId(subject._id)}
                  >
                    <CardContent>
                      <Typography variant="h6" sx={{ mb: 1 }}>{subject.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {subject.count} Grade{subject.count !== 1 ? 's' : ''}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box>
              <Button sx={{ mb: 2 }} onClick={() => setSelectedSubjectId(null)} startIcon={<ArrowBack />}>
                Back to Subjects
              </Button>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Assignment</TableCell>
                      <TableCell>Subject</TableCell>
                      <TableCell>Score</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Feedback</TableCell>
                      <TableCell>Graded</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {grades
                      .filter(g => (g.assignment?.subject?._id || 'general') === selectedSubjectId)
                      .map((grade) => (
                      <TableRow key={grade._id}>
                        <TableCell>{grade.assignment?.title || 'Unknown'}</TableCell>
                        <TableCell>{grade.assignment?.subject?.name || 'General'}</TableCell>
                        <TableCell>{grade.score !== undefined && grade.maxScore !== undefined ? `${grade.score}/${grade.maxScore}` : grade.score ?? 'Pending'}</TableCell>
                        <TableCell>{grade.status}</TableCell>
                        <TableCell>{grade.feedback || 'No feedback yet'}</TableCell>
                        <TableCell>{grade.gradedAt ? new Date(grade.gradedAt).toLocaleDateString() : 'Not graded'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default StudentGrades;
